
import { useState } from 'react';

interface AdminHeaderProps {
  onQuickAction?: () => void;
}

const AdminHeader = ({ onQuickAction }: AdminHeaderProps) => {
  const [showNotifications, setShowNotifications] = useState(false);
  const [showQuickActions, setShowQuickActions] = useState(false);

  const notifications = [
    {
      id: 1,
      type: 'security',
      title: 'Security Alert',
      message: 'Multiple failed login attempts detected',
      time: '5 minutes ago',
      unread: true
    },
    {
      id: 2,
      type: 'system',
      title: 'System Update',
      message: 'Platform maintenance scheduled for tonight',
      time: '1 hour ago',
      unread: true
    },
    {
      id: 3,
      type: 'company',
      title: 'New Company Registration',
      message: 'TechCorp Solutions has requested platform access',
      time: '2 hours ago',
      unread: false
    }
  ];

  const quickActions = [
    {
      id: 'create-company',
      label: 'Create Company',
      icon: 'ri-building-add-line',
      description: 'Add new company platform'
    },
    {
      id: 'invite-user',
      label: 'Invite User',
      icon: 'ri-user-add-line',
      description: 'Send user invitation'
    },
    {
      id: 'system-backup',
      label: 'System Backup',
      icon: 'ri-database-line',
      description: 'Create system backup'
    },
    {
      id: 'security-scan',
      label: 'Security Scan',
      icon: 'ri-shield-check-line',
      description: 'Run security audit'
    }
  ];

  const unreadCount = notifications.filter(n => n.unread).length;

  const handleQuickAction = (actionId: string) => {
    console.log('Quick action:', actionId);
    setShowQuickActions(false);
    if (onQuickAction) {
      onQuickAction();
    }
  };

  const markAllAsRead = () => {
    console.log('Marking all notifications as read');
  };

  return (
    <header className="bg-white border-b border-gray-200 px-6 py-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Administrator Dashboard</h1>
          <p className="text-sm text-gray-600">Manage company platforms and user accounts</p>
        </div>

        <div className="flex items-center space-x-4">
          {/* Search */}
          <div className="relative">
            <input
              type="text"
              placeholder="Search companies, users..."
              className="w-64 pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
            />
            <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
          </div>

          {/* Notifications */}
          <div className="relative">
            <button
              onClick={() => setShowNotifications(!showNotifications)}
              className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
            >
              <i className="ri-notification-line text-xl"></i>
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </button>

            {/* Notifications Dropdown */}
            {showNotifications && (
              <div className="absolute right-0 mt-2 w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Notifications</h3>
                  <button
                    onClick={markAllAsRead}
                    className="text-sm text-red-600 hover:text-red-700 font-medium"
                  >
                    Mark all read
                  </button>
                </div>
                <div className="max-h-64 overflow-y-auto">
                  {notifications.map((notification) => (
                    <div
                      key={notification.id}
                      className={`p-4 border-b border-gray-100 hover:bg-gray-50 ${
                        notification.unread ? 'bg-red-50' : ''
                      }`}
                    >
                      <div className="flex items-start">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 ${
                          notification.type === 'security' ? 'bg-red-100' :
                          notification.type === 'system' ? 'bg-blue-100' : 'bg-green-100'
                        }`}>
                          <i className={`${
                            notification.type === 'security' ? 'ri-shield-line text-red-600' :
                            notification.type === 'system' ? 'ri-settings-line text-blue-600' : 'ri-building-line text-green-600'
                          }`}></i>
                        </div>
                        <div className="flex-1">
                          <h4 className="text-sm font-medium text-gray-900">{notification.title}</h4>
                          <p className="text-sm text-gray-600 mt-1">{notification.message}</p>
                          <p className="text-xs text-gray-500 mt-1">{notification.time}</p>
                        </div>
                        {notification.unread && (
                          <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="p-3 border-t border-gray-200">
                  <button className="w-full text-center text-sm text-red-600 hover:text-red-700 font-medium">
                    View all notifications
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div className="relative">
            <button
              onClick={() => setShowQuickActions(!showQuickActions)}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium whitespace-nowrap"
            >
              <i className="ri-add-line mr-2"></i>
              Quick Action
            </button>

            {/* Quick Actions Dropdown */}
            {showQuickActions && (
              <div className="absolute right-0 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                <div className="p-3 border-b border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-900">Quick Actions</h3>
                </div>
                <div className="py-2">
                  {quickActions.map((action) => (
                    <button
                      key={action.id}
                      onClick={() => handleQuickAction(action.id)}
                      className="w-full flex items-center px-4 py-3 hover:bg-gray-50 text-left"
                    >
                      <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center mr-3">
                        <i className={`${action.icon} text-gray-600`}></i>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900">{action.label}</div>
                        <div className="text-xs text-gray-500">{action.description}</div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Click outside to close dropdowns */}
      {(showNotifications || showQuickActions) && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => {
            setShowNotifications(false);
            setShowQuickActions(false);
          }}
        ></div>
      )}
    </header>
  );
};

export default AdminHeader;
